import React from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {
    addSubOrder,
    addSubOrderItem,
    deleteSubOrderItem,
    getOrder,
    updateOrder,
    updateSubOrderItem
} from '../../api/orders'
import {listDeliveryLocations} from '../../api/deliveryLocations'
import {listShops} from '../../api/shops'
import {listShopItemMappings} from '../../api/shopShopItemMap'
import {App as AntdApp, Button, Card, Divider, Form, Input, message, Modal, Select, Space, Typography} from 'antd'
import {formatAmount} from "../../utils/amount.js";

export default function AdminOrderEdit() {
    const {orderId} = useParams()
    const navigate = useNavigate()
    const qc = useQueryClient()

    const {data: order, isLoading, isError, error, refetch} = useQuery({
        queryKey: ['adminOrder', orderId],
        enabled: !!orderId,
        queryFn: () => getOrder(orderId),
    })

    // Delivery location name now comes from nested user.deliveryLocation
    const locName = React.useMemo(() => {
        const dl = order?.user?.deliveryLocation
        return dl?.name || ''
    }, [order])

    return (
        <div style={{maxWidth: 1100, margin: '40px auto', padding: '0 16px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
                <Typography.Title level={3}
                                  style={{margin: 0}}>{order ? `الطلب #${order.id}` : 'الطلب'}</Typography.Title>
                <Button onClick={() => navigate('/admin/orders')}>عودة</Button>
            </div>

            {isLoading && <Card>...جارِ التحميل</Card>}
            {isError && (
                <Card>
                    <Typography.Text type="danger">خطأ: {String(error?.message || 'فشل')}</Typography.Text>
                    <div><Button onClick={() => refetch()}>إعادة المحاولة</Button></div>
                </Card>
            )}

            {!isLoading && !isError && order && (
                <Card>
                    <div style={{display: 'grid', gap: 12}}>
                        <div style={{display: 'grid', gap: 4}}>
                            <Typography.Text>الاسم: {order.user?.name || '—'}</Typography.Text>
                            <Typography.Text>الجوال: {order.user?.phone || '—'}</Typography.Text>
                            <Typography.Text>العنوان: {order.user?.fullAddress || '—'}</Typography.Text>
                            <Typography.Text>المنطقة: {locName || '—'}</Typography.Text>
                            <Typography.Text>الإجمالي: {order.totalAfterDiscount}</Typography.Text>
                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                <Typography.Text>الحالة:</Typography.Text>
                                <StatusSelect order={order} onChanged={() => {
                                    qc.invalidateQueries({queryKey: ['adminOrder', orderId]});
                                    qc.invalidateQueries({queryKey: ['adminOrders']});
                                }}/>
                            </div>
                        </div>

                        <OrderNotesEditor order={order} onChanged={() => {
                            qc.invalidateQueries({queryKey: ['adminOrder', orderId]});
                            qc.invalidateQueries({queryKey: ['adminOrders']});
                        }} />

                        <ManageSubOrders order={order} onChanged={() => {
                            qc.invalidateQueries({queryKey: ['adminOrder', orderId]});
                            qc.invalidateQueries({queryKey: ['adminOrders']});
                        }}/>
                    </div>
                </Card>
            )}
        </div>
    )
}

function StatusSelect({order, onChanged}) {
    const [loading, setLoading] = React.useState(false)
    const [value, setValue] = React.useState(order.status)

    async function save() {
        try {
            setLoading(true)
            await updateOrder(order.id, {status: value})
            message.success('تم تحديث الحالة')
            onChanged?.()
        } catch (e) {
            console.error(e)
            message.error('فشل تحديث الحالة')
        } finally {
            setLoading(false)
        }
    }

    const LABELS = {
        'new': 'جديد',
        'confirmed with shops': 'تم التأكيد مع المتاجر',
        'notified driver': 'تم إشعار السائق',
        'delivered': 'تم التوصيل',
        'cancelled': 'أُلغي',
    }

    return (
        <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            <select value={value} onChange={e => setValue(e.target.value)} disabled={loading}>
                {['new', 'confirmed with shops', 'notified driver', 'delivered', 'cancelled'].map(s => (
                    <option key={s} value={s}>{LABELS[s] || s}</option>
                ))}
            </select>
            <Button size="small" onClick={save} loading={loading}>حفظ</Button>
        </div>
    )
}

function ManageSubOrders({order, onChanged}) {
    // Sub-order creation
    const [showSubModal, setShowSubModal] = React.useState(false)
    const [subForm] = Form.useForm()

    // Item editing
    const [showItemModal, setShowItemModal] = React.useState(false)
    const [editingItem, setEditingItem] = React.useState(null) // { subId, shopId, item } or { subId, shopId }
    const [itemForm] = Form.useForm()
    const [optionsLoading, setOptionsLoading] = React.useState(false)
    const [shopItemOptions, setShopItemOptions] = React.useState([])

    const loadShopItemOptions = React.useCallback(async (shopId) => {
        if (!shopId) {
            setShopItemOptions([]);
            return
        }
        setOptionsLoading(true)
        try {
            const maps = await listShopItemMappings({shopId})
            const opts = (maps || [])
                .filter(m => m?.available !== false)
                .map(m => ({
                    value: m.id,
                    label: `${m?.item?.nameAr || m?.item?.name || `#${m?.item?.id}`} — ${m?.sellPrice ?? ''}`
                }))
            setShopItemOptions(opts)
        } catch (e) {
            console.error(e)
            setShopItemOptions([])
        } finally {
            setOptionsLoading(false)
        }
    }, [])

    // Load shops for dropdown
    const {data: shops} = useQuery({
        queryKey: ['shopsAll'],
        queryFn: listShops,
    })

    function openNewSub() {
        subForm.setFieldsValue({shopId: undefined, pickupHour: undefined})
        setShowSubModal(true)
    }

    async function submitSub(values) {
        try {
            await addSubOrder(order.id, {shopId: Number(values.shopId), pickupHour: values.pickupHour || null})
            message.success('تم إنشاء طلب فرعي')
            setShowSubModal(false)
            subForm.resetFields()
            onChanged?.()
        } catch (e) {
            console.error(e)
            message.error('تعذر حفظ الطلب الفرعي')
        }
    }

    function openEditItem(sub, it) {
        setEditingItem({subId: sub.id, shopId: sub.shopId, item: it})
        itemForm.setFieldsValue({mapId: it.mapId, quantity: it.quantity})
        loadShopItemOptions(sub.shopId)
        setShowItemModal(true)
    }

    function openNewItem(sub) {
        setEditingItem({subId: sub.id, shopId: sub.shopId, item: null})
        itemForm.setFieldsValue({mapId: undefined, quantity: 1})
        loadShopItemOptions(sub.shopId)
        setShowItemModal(true)
    }

    async function submitItem(values) {
        try {
            const subId = editingItem.subId
            if (editingItem.item?.id) {
                await updateSubOrderItem(order.id, subId, editingItem.item.id, {
                    mapId: values.mapId,
                    quantity: Number(values.quantity)
                })
                message.success('تم تحديث العنصر')
            } else {
                await addSubOrderItem(order.id, subId, {mapId: values.mapId, quantity: Number(values.quantity)})
                message.success('تمت إضافة العنصر')
            }
            setShowItemModal(false)
            setEditingItem(null)
            itemForm.resetFields()
            onChanged?.()
        } catch (e) {
            console.error(e)
            message.error('تعذر حفظ العنصر')
        }
    }

    const { modal } = AntdApp.useApp()

    async function removeItem(sub, it) {
        console.debug('[AdminOrderEdit] delete click', {orderId: order?.id, subId: sub?.id, itemId: it?.id})
        modal.confirm({
            title: 'حذف العنصر؟',
            onOk: async () => {
                try {
                    console.debug('[AdminOrderEdit] confirming delete', {
                        orderId: order?.id,
                        subId: sub?.id,
                        itemId: it?.id
                    })
                    await deleteSubOrderItem(order.id, sub.id, it.id)
                    message.success('تم حذف العنصر')
                    onChanged?.()
                } catch (e) {
                    console.error(e)
                    message.error('فشل حذف العنصر')
                }
            }
        })
    }

    return (
        <div style={{background: '#fafafa', padding: 8, borderRadius: 6}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography.Text strong>الطلبات الفرعية</Typography.Text>
                <Button size="small" onClick={openNewSub}>+ إضافة طلب فرعي</Button>
            </div>
            <Divider style={{margin: '8px 0'}}/>
            {(order.subOrders || []).length === 0 && (
                <Typography.Text type="secondary">لا توجد طلبات فرعية</Typography.Text>
            )}
            {(order.subOrders || []).map(sub => (
                <Card key={sub.id} size="small" style={{marginBottom: 8}} title={
                    <span>متجر: {sub.shopNameAr} | المجموع: {sub.totalAfterDiscount} {sub.pickupHour ? ` | ${sub.pickupHour}` : ''}</span>}>
                    <div style={{display: 'grid', gap: 6}}>
                        {(sub.items || []).map(it => (
                            <div key={it.id}
                                 style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <div>
                                    <strong>{it.itemNameAr}</strong> — [{it.quantity}]
                                    × {formatAmount(it.unitPrice)} = {formatAmount(it.rowTotal)}
                                </div>
                                <Space>
                                    <Button size="small" onClick={(e) => {
                                        e?.stopPropagation?.();
                                        openEditItem(sub, it)
                                    }}>تعديل</Button>
                                    <Button size="small" danger onClick={(e) => {
                                        e?.stopPropagation?.();
                                        removeItem(sub, it)
                                    }}>حذف</Button>
                                </Space>
                            </div>
                        ))}
                        <div>
                            <Button size="small" onClick={() => openNewItem(sub)}>+ إضافة عنصر</Button>
                        </div>
                    </div>
                </Card>
            ))}

            {/* Create SubOrder Modal */}
            <Modal
                open={showSubModal}
                onCancel={() => {
                    setShowSubModal(false);
                    subForm.resetFields()
                }}
                onOk={() => subForm.submit()}
                okText="إنشاء"
                title="إضافة طلب فرعي"
            >
                <Form form={subForm} layout="vertical" onFinish={submitSub}>
                    <Form.Item label="المتجر" name="shopId" rules={[{required: true, message: 'يرجى اختيار متجر'}]}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            options={(shops || []).map(s => ({label: s.nameAr || s.name || `#${s.id}`, value: s.id}))}
                        />
                    </Form.Item>
                    <Form.Item label="ساعة الاستلام (HH:mm)" name="pickupHour">
                        <Input placeholder="مثال: 13:30"/>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Item Modal */}
            <Modal
                open={showItemModal}
                onCancel={() => {
                    setShowItemModal(false);
                    setEditingItem(null)
                }}
                onOk={() => itemForm.submit()}
                okText={editingItem?.item?.id ? 'حفظ' : 'إضافة'}
                title={editingItem?.item?.id ? 'تعديل عنصر' : 'إضافة عنصر'}
            >
                <Form form={itemForm} layout="vertical" onFinish={submitItem}>
                    <Form.Item label="العنصر (الاسم والسعر)" name="mapId"
                               rules={[{required: true, message: 'يرجى اختيار عنصر'}]}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            options={shopItemOptions}
                            loading={optionsLoading}
                            placeholder="اختر العنصر بالسعر"
                        />
                    </Form.Item>
                    <Form.Item label="الكمية" name="quantity"
                               rules={[{required: true, min: 1, type: 'number', transform: v => Number(v)}]}>
                        <Input type="number"/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

function OrderNotesEditor({ order, onChanged }) {
    const [value, setValue] = React.useState(order?.notes || '')
    const [loading, setLoading] = React.useState(false)

    React.useEffect(() => {
        setValue(order?.notes || '')
    }, [order?.notes])

    async function save() {
        try {
            setLoading(true)
            await updateOrder(order.id, { notes: (value || '').trim() || null })
            message.success('تم حفظ الملاحظات')
            onChanged?.()
        } catch (e) {
            console.error(e)
            message.error('فشل حفظ الملاحظات')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div style={{ background: '#fafafa', padding: 8, borderRadius: 6 }}>
            <Typography.Text strong>ملاحظات الطلب</Typography.Text>
            <div style={{ marginTop: 6 }}>
                <textarea
                    className="ant-input"
                    style={{ width: '100%', minHeight: 80, resize: 'vertical' }}
                    placeholder="مثال: الساندويتش بدون مخلل"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                />
            </div>
            <div style={{ marginTop: 6 }}>
                <Button size="small" type="primary" onClick={save} loading={loading}>حفظ الملاحظات</Button>
            </div>
        </div>
    )
}
